import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FocusRing } from 'react-aria';
import { defineMessages, useIntl } from 'react-intl';
import { v4 as uuid } from '@lukeed/uuid';
import classNames from 'classnames';

import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    AlgoliaAnalyticsEventAction,
    AlgoliaAnalyticsEventPage,
    AlgoliaAnalyticsEventSource,
    sendAlgoliaFiltersClickedEvent,
    sendAlgoliaFiltersViewedEvent,
} from '@jsmdg/tracking';
import {
    CloseIcon,
    Input,
    type LocationErrorType,
    MapMarkerIcon,
    type MappedSuggestion,
    type PlaceDetailResult,
    PureButton,
    useClickOutside,
    useLocationFilter,
} from '@jsmdg/yoshi';
import { type FragmentContext } from '../../../../../../shared/types/fragmentContext';
import { type Filter } from '../../../../../../shared/types/search';
import { NO_LOCATION_NAME } from '../../../../../constants/locationFilter';
import {
    trackLocateMeError,
    trackLocationTypeInClicked,
    trackLocationTypeInError,
    trackResetLocationClicked,
} from '../../../../../helper/locationFilter.tracking';
import { TTLCacheWrapper } from '../../../../../helper/ttlCacheWrapper';
import { getActiveFilters } from '../../../../../hooks/getActiveFilters';
import { useInsightsClient } from '../../../../../hooks/useInsightsClient';
import { SearchReducerActionType } from '../../../../../reducers/searchReducer';
import { type SearchReducerValue } from '../../../../../types/searchReducer';
import { SuggestionList } from '../../../../SuggestionList';
import { CurrentLocationButton } from './CurrentLocationButton';
import styles from './LocationFilter.module.scss';

const DEFAULT_DISTANCE = 100;
const LOCATION_CACHE_TTL_SECONDS = 86_400;
const SUGGESTIONS_CACHE_STORAGE_KEY = 'evelin.location.suggestions';
const DETAILS_CACHE_STORAGE_KEY = 'evelin.location.details';

const createLocationCaches = () => ({
    suggestions: new TTLCacheWrapper<MappedSuggestion[]>(
        SUGGESTIONS_CACHE_STORAGE_KEY,
        LOCATION_CACHE_TTL_SECONDS,
    ),
    details: new TTLCacheWrapper<PlaceDetailResult>(
        DETAILS_CACHE_STORAGE_KEY,
        LOCATION_CACHE_TTL_SECONDS,
    ),
});

const locationFilterMessages = defineMessages({
    placeholderLong: { defaultMessage: 'Wo (PLZ oder Ort)' },
    placeholderShort: { defaultMessage: 'Wo?' },
    allLocationsPlaceholder: { defaultMessage: 'Alle Standorte' },
    invalidLocation: { defaultMessage: 'Vertippt? Diesen Ort konnten wir leider nicht finden.' },
    technicalProblem: {
        defaultMessage:
            'Leider ist die Ortsfilterung aus technischen Gründen momentan nicht verfügbar.',
    },
    currentLocation: { defaultMessage: 'Dein Standort' },
    currentLocationErrorMessage: {
        defaultMessage: 'Dein Standort konnte leider nicht ermittelt werden.',
    },
    errorCloseButton: { defaultMessage: 'Fehlermeldung schließen' },
    clearLocation: { defaultMessage: 'Schließen' },
    a11yLocationFilter: { defaultMessage: 'Standort eingeben' },
    a11yCloseButton: { defaultMessage: 'Standort entfernen' },
});

const getGeoErrorMessage = (
    error: GeolocationPositionError | undefined,
    message: string,
): string => {
    if (
        typeof window !== 'undefined' &&
        typeof window.GeolocationPositionError === 'function' &&
        error instanceof window.GeolocationPositionError
    ) {
        return message;
    }

    return '';
};

const preventSubmit = (e: React.FormEvent) => e.preventDefault();

type LocationFilterProps = {
    readonly filter?: Filter;
    readonly locationName?: string;
    readonly shouldReset?: boolean;
    readonly geoLocationError?: GeolocationPositionError;
    readonly onSubmit: (type: SearchReducerActionType, value?: SearchReducerValue) => void;
    readonly scrollToElement: () => void;
};

type ErrorTooltipProps = {
    readonly message: string;
    readonly closeButtonLabel: string;
    readonly onClose: () => void;
};

const ErrorTooltip = ({ closeButtonLabel, message, onClose }: ErrorTooltipProps): JSX.Element => (
    <div className={classNames(styles.locationErrorTooltip, 'position-absolute')}>
        <PureButton
            onClick={onClose}
            className={classNames(styles.tooltipButton, 'position-absolute p-0 m-0')}
            aria-label={closeButtonLabel}
        >
            <CloseIcon ariaHidden />
        </PureButton>
        <p role="alert">{message}</p>
    </div>
);

type InputRightComponentProps = {
    readonly inputValue: string;
    readonly clearLabel: string;
    readonly onClear: () => void;
    readonly showLocationButton: boolean;
    readonly onSubmit: (type: SearchReducerActionType, value?: SearchReducerValue) => void;
};

const InputRightComponent = ({
    clearLabel,
    inputValue,
    onClear,
    onSubmit,
    showLocationButton,
}: InputRightComponentProps): JSX.Element => (
    <>
        {inputValue && (
            <PureButton aria-label={clearLabel} className={styles.clearButton} onClick={onClear}>
                <CloseIcon ariaHidden />
            </PureButton>
        )}
        {showLocationButton && <CurrentLocationButton location={inputValue} onSubmit={onSubmit} />}
    </>
);

const LocationFilter = ({
    filter,
    geoLocationError,
    locationName = '',
    onSubmit,
    scrollToElement,
    shouldReset = false,
}: LocationFilterProps): JSX.Element => {
    const intl = useIntl();
    const { algoliaTracking, getPlacesDetail, getPlacesSuggestions } =
        useFragmentContext<FragmentContext>();
    const insightsClientRef = useInsightsClient();
    const inputId = useId();
    const filterRef = useRef<HTMLFormElement>(null);
    const [isErrorTooltipVisible, setIsErrorTooltipVisible] = useState(false);
    // eslint-disable-next-line react/hook-use-state -- create-once value, never reassigned
    const [locationCaches] = useState(createLocationCaches);

    const formattedMessages = useMemo(
        () => ({
            placeholderLong: intl.formatMessage(locationFilterMessages.placeholderLong),
            placeholderShort: intl.formatMessage(locationFilterMessages.placeholderShort),
            allLocationsPlaceholder: intl.formatMessage(
                locationFilterMessages.allLocationsPlaceholder,
            ),
            invalidLocation: intl.formatMessage(locationFilterMessages.invalidLocation),
            technicalProblem: intl.formatMessage(locationFilterMessages.technicalProblem),
            currentLocation: intl.formatMessage(locationFilterMessages.currentLocation),
            currentLocationErrorMessage: intl.formatMessage(
                locationFilterMessages.currentLocationErrorMessage,
            ),
            errorCloseButton: intl.formatMessage(locationFilterMessages.errorCloseButton),
            clearLocation: intl.formatMessage(locationFilterMessages.clearLocation),
            a11yLocationFilter: intl.formatMessage(locationFilterMessages.a11yLocationFilter),
            a11yCloseButton: intl.formatMessage(locationFilterMessages.a11yCloseButton),
        }),
        [intl],
    );

    const effectiveLocationName =
        locationName === NO_LOCATION_NAME ? formattedMessages.currentLocation : locationName;

    const sendLocationFilterEvent = useCallback(
        async (action: AlgoliaAnalyticsEventAction, values: string[]): Promise<void> => {
            if (!values.length || !algoliaTracking?.userToken || !algoliaTracking.indexName) {
                return;
            }

            await window.yieldToMainThread?.();

            try {
                const insightsClient = (await insightsClientRef.current)?.default;

                if (!insightsClient) {
                    return;
                }

                const eventName = {
                    action,
                    page: AlgoliaAnalyticsEventPage.Home,
                    source: AlgoliaAnalyticsEventSource.ExperienceFinder,
                };
                const filters = values.filter(Boolean).map(value => `location:${value}`);
                const sendFilters =
                    action === AlgoliaAnalyticsEventAction.FilterViewed
                        ? sendAlgoliaFiltersViewedEvent
                        : sendAlgoliaFiltersClickedEvent;

                for (let start = 0; start < filters.length; start += 10) {
                    sendFilters({
                        eventName,
                        filters: filters.slice(start, start + 10),
                        indexName: algoliaTracking.indexName,
                        insightsClient,
                    });
                }
            } catch (error) {
                logToSentry(error instanceof Error ? error : new Error('Algolia Insights failed'));
            }
        },
        [algoliaTracking?.indexName, algoliaTracking?.userToken, insightsClientRef],
    );

    const handleLocationSelect = useCallback(
        (detail: PlaceDetailResult & { label: string }) => {
            onSubmit(SearchReducerActionType.Location, {
                lat: detail.lat,
                long: detail.lng,
                name: detail.label,
                distance: DEFAULT_DISTANCE,
            });
            void sendLocationFilterEvent(AlgoliaAnalyticsEventAction.FilterClicked, [detail.label]);
            const activeFiltersString = getActiveFilters(filter, ['Location', 'distance']);
            trackLocationTypeInClicked(
                `${detail.label}_${DEFAULT_DISTANCE}km`,
                detail.label,
                activeFiltersString,
            );
            filterRef.current?.querySelector('input')?.blur();
        },
        [filter, onSubmit, sendLocationFilterEvent],
    );

    const handleLocationClear = useCallback(() => {
        const activeFiltersString = getActiveFilters(filter);
        trackResetLocationClicked('ResetLocation', activeFiltersString);
        onSubmit(SearchReducerActionType.LocationFilterReset);
    }, [filter, onSubmit]);

    const handleError = useCallback((_type: LocationErrorType, message: string) => {
        trackLocationTypeInError('InvalidLocation', message);
    }, []);

    const {
        closeDropdown,
        errorMessage,
        inputValue,
        isActive,
        onClear,
        onInputChange,
        onKeyDown,
        onSuggestionSelect,
        selectedIndex,
        setIsActive,
        setSelectedIndex,
        suggestions,
    } = useLocationFilter({
        getPlacesSuggestions,
        getPlacesDetail,
        generateSessionToken: uuid,
        messages: formattedMessages,
        formatMessage: (msg: string) => msg,
        cache: locationCaches,
        location: effectiveLocationName,
        shouldReset,
        onLocationSelect: handleLocationSelect,
        onLocationClear: handleLocationClear,
        onError: handleError,
    });

    const geoLocationErrorMessage = getGeoErrorMessage(
        geoLocationError,
        formattedMessages.currentLocationErrorMessage,
    );

    const displayedErrorMessage = geoLocationErrorMessage || errorMessage;

    useEffect(() => {
        if (geoLocationErrorMessage) {
            trackLocateMeError(
                'LocateMe blocked',
                'Error - LocateMe blocked',
                geoLocationErrorMessage,
            );
        }
    }, [geoLocationErrorMessage]);

    useEffect(() => {
        if (displayedErrorMessage) {
            setIsErrorTooltipVisible(true);
        }
    }, [displayedErrorMessage]);

    const isSuggestionListOpen = Boolean(suggestions) && isActive;
    const lastViewedSuggestionsKeyRef = useRef<string>('');

    useEffect(() => {
        if (!isSuggestionListOpen) {
            lastViewedSuggestionsKeyRef.current = '';
            return;
        }

        if (!suggestions?.length) {
            return;
        }

        const suggestionLabels = suggestions.map(
            suggestion =>
                `${suggestion.title ?? ''}${suggestion.subTitle ? `, ${suggestion.subTitle}` : ''}`,
        );
        const suggestionsKey = suggestionLabels.join('|');

        if (lastViewedSuggestionsKeyRef.current === suggestionsKey) {
            return;
        }

        lastViewedSuggestionsKeyRef.current = suggestionsKey;
        void sendLocationFilterEvent(AlgoliaAnalyticsEventAction.FilterViewed, suggestionLabels);
    }, [isSuggestionListOpen, sendLocationFilterEvent, suggestions]);

    useClickOutside(filterRef, () => {
        if (isSuggestionListOpen) {
            closeDropdown();
        }
    });

    const handleInputChange = (event: React.FormEvent<HTMLInputElement>): void => {
        onInputChange((event.target as HTMLInputElement).value);
    };

    const toggleActive = (): void => setIsActive(!isActive);

    const closeErrorTooltip = (): void => setIsErrorTooltipVisible(false);

    const mappedSuggestions =
        suggestions?.map(suggestion => ({
            title: suggestion.title,
            subTitle: suggestion.subTitle || '',
            placeId: suggestion.id,
        })) ?? null;

    const wrapperClasses = classNames('d-flex flex-sm-column w-100 p-0', styles.wrapper, {
        [styles.isActive]: isActive || inputValue,
    });

    const inputClasses = classNames(
        styles.input,
        'p-0 pl-0-5x pl-sm-2x pr-5x my-0-5x mr-0-5x m-sm-0',
    );

    return (
        <form
            autoComplete="off"
            className="position-relative"
            onSubmit={preventSubmit}
            ref={filterRef}
        >
            <div className={wrapperClasses}>
                <div className="d-flex align-items-start">
                    <MapMarkerIcon
                        ariaHidden
                        className={classNames(styles.icon, 'ml-1x mr-0-5x')}
                    />
                    <p className={classNames(styles.label, 'd-none d-sm-block fw-semibold mb-0')}>
                        {formattedMessages.placeholderShort}
                    </p>
                </div>
                <FocusRing focusRingClass={styles.focusRing}>
                    <Input
                        disabledFocusState
                        className={classNames('d-flex', styles.inputWrapper)}
                        inputClasses={inputClasses}
                        name={inputId}
                        onClick={toggleActive}
                        onInput={handleInputChange}
                        onKeyDown={onKeyDown}
                        onFocus={scrollToElement}
                        placeholder={formattedMessages.allLocationsPlaceholder}
                        hideLabel
                        value={inputValue}
                        rightComponent={
                            <InputRightComponent
                                inputValue={inputValue}
                                clearLabel={formattedMessages.clearLocation}
                                onClear={onClear}
                                showLocationButton={!displayedErrorMessage}
                                onSubmit={onSubmit}
                            />
                        }
                        aria-label={formattedMessages.a11yLocationFilter}
                    />
                </FocusRing>
            </div>
            {isErrorTooltipVisible && displayedErrorMessage && (
                <ErrorTooltip
                    message={displayedErrorMessage}
                    closeButtonLabel={formattedMessages.errorCloseButton}
                    onClose={closeErrorTooltip}
                />
            )}
            {isSuggestionListOpen && (
                <SuggestionList
                    selected={selectedIndex}
                    setSelected={setSelectedIndex}
                    suggestions={mappedSuggestions}
                    suggestionClick={onSuggestionSelect}
                    className="w-100"
                />
            )}
        </form>
    );
};

export { LocationFilter };
